<script setup lang="ts">
import { type PropType, computed } from 'vue'
import { useStore } from '@nanostores/vue'
import { type IMercariBrandDetail } from '@/types/MercariBrandDetail'
import { replaceBrand } from '@/lib/mercari'
import MercariBrandMerchantItem from '@/pages/mercari-brand/_mercariBrandMerchantItem.vue'
import { useBreakpoints } from '@/lib/utils'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import { isLogin } from '@/store/userStore'
import { getMcrRootUrl } from '@/lib/siteBaseUrl'
import Skeleton from '@/components/Skeleton.vue'
import { checkIsShowMercariBrandPhase2 } from '@/lib/checkService'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

interface IMerchantListContent {
	title: string
	subTitle: string
	officialText: string
	buttonTextCard: string
	buttonTextLogin: string
	buttonTextLogout: string
	discountText: string
}

const isShowMercariBrandPhase2 = checkIsShowMercariBrandPhase2()
const isLoginGlobal = useStore(isLogin)

const props = defineProps({
	pageContent: {
		type: Object as PropType<{ merchantList: IMerchantListContent }>,
		required: true,
	},
	brandDetail: {
		type: Object as PropType<IMercariBrandDetail | null>,
	},
})

const currentMerchantListTitle = computed(() => {
	return replaceBrand(
		props.pageContent?.merchantList?.title,
		props.brandDetail?.name ?? '',
	)
})

const currentMerchantListSubTitle = computed(() => {
	return replaceBrand(
		props.pageContent?.merchantList?.subTitle,
		props.brandDetail?.name ?? '',
	)
})

const currentButtonText = computed(() => {
	return isLoginGlobal.value
		? props.pageContent?.merchantList?.buttonTextLogin
		: props.pageContent?.merchantList?.buttonTextLogout
})

const { isTabletLandscape } = useBreakpoints()

const isLoading = computed(() => {
	return !props.brandDetail
})

const MAX_DISPLAY_MERCHANTS = 6
const MIN_MERCHANTS_TO_SHOW = 3

const currentMerchantList = computed(() => {
	if (!props.brandDetail?.merchant_listing) return []
	return props.brandDetail.merchant_listing.slice(0, MAX_DISPLAY_MERCHANTS)
})

const isShowMerchantSection = computed(() => {
	return (
		isShowMercariBrandPhase2 &&
		(!props.brandDetail ||
			props.brandDetail.merchant_listing.length >= MIN_MERCHANTS_TO_SHOW)
	)
})

function handleButtonClick() {
	if (!isLoginGlobal.value) {
		showAuthPanel()
	}
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariBrand,
		button_name: isLoginGlobal.value
			? 'Add Request - Merchant List'
			: 'Sign up - Merchant List',
	})
}

function showAuthPanel() {
	window.BsHeaderVm.showAuthPanel()
}
</script>

<template>
	<div
		class="bs-container py-distant-acquaintances"
		v-if="isShowMerchantSection"
	>
		<div class="col-span-12 text-center">
			<div class="mb-32px mdl:mb-0">
				<Skeleton
					v-if="isLoading"
					class="mx-auto h-[32px] w-[240px] mdl:h-[40px]"
				/>
				<h3 v-else class="typography-h3-str text-n-950">
					{{ currentMerchantListTitle }}
				</h3>
			</div>

			<div class="mb-32px mt-20px hidden mdl:block">
				<Skeleton v-if="isLoading" class="mx-auto h-[24px] w-[300px]" />
				<p v-else class="typography-s1-reg">
					{{ currentMerchantListSubTitle }}
				</p>
			</div>

			<div
				v-if="isShowMerchantSection"
				class="grid grid-cols-1 gap-16px mdl:grid-cols-3"
			>
				<template v-if="isLoading">
					<MercariBrandMerchantItem
						v-for="i in MAX_DISPLAY_MERCHANTS"
						:key="i"
						:isLoading="isLoading"
					/>
				</template>
				<template v-else>
					<MercariBrandMerchantItem
						v-for="merchant in currentMerchantList"
						:key="merchant.merchant_name"
						:title="merchant.merchant_name"
						:region="merchant.region"
						:isOfficial="merchant.is_official_shop"
						:officialText="pageContent.merchantList.officialText"
						:buttonText="pageContent.merchantList.buttonTextCard"
						:buttonLink="merchant.domain"
						:discountText="pageContent.merchantList.discountText"
						:averageDiscount="merchant.average_discount"
						:isTablet="isTabletLandscape"
						:isLoading="false"
					/>
				</template>
			</div>

			<div class="mx-auto mt-32px inline-block">
				<component
					:is="isLoginGlobal ? 'a' : 'button'"
					:href="isLoginGlobal ? `${getMcrRootUrl()}/buyforyou/new` : undefined"
					:target="isLoginGlobal ? '_blank' : undefined"
					@click="handleButtonClick"
				>
					<BaseButton size="medium">
						{{ currentButtonText }}
					</BaseButton>
				</component>
			</div>
		</div>
	</div>
</template>
